import { defineStore } from 'pinia';

/**
 * ช่องทางติดต่อของเรา (ผู้ขายระบบ) — ค่าเดียวกันทุกร้าน ไม่เคยต่างรายร้าน
 * จึงฟิกซ์ไว้ตรงนี้ ไม่ได้เก็บใน DB (ดู ../silver-spoon-api/docs/license-expiry.md §3)
 *
 * ช่องไหนเว้นว่าง = ไม่โชว์ช่องนั้น (แถบเตือน / popup / หน้าล็อก อ่านจากตรงนี้ทั้งหมด)
 * LINE OA ตัวเดียวกับเว็บการตลาด (../sivapos-site/src/data/site.ts → lineId) — เปลี่ยนต้องแก้ทั้งสองที่
 * plansUrl = หน้าราคาบนเว็บการตลาด (../sivapos-site/src/pages/pricing.astro — มีตารางเทียบแพ็กครบ)
 *   ราคา/ฟีเจอร์ในนั้น (site.ts → plans, planFeatures, reports) ต้องตรงกับ PlanSeeder ของ API
 *
 * phone ปิดไว้ก่อน (ตัดสิน 2026-09-29): ให้ร้านติดต่อทาง LINE อย่างเดียว — รู้ทันทีว่าร้านไหนและตอบเป็นลายลักษณ์อักษร
 * จะเปิดเบอร์กลับ ใส่ '098-2299881' ตรงนี้ที่เดียว ปุ่มโทรขึ้นเองทุกจุด
 */
export const SUPPORT_CONTACT = {
  line: '@103nzapc',
  phone: '',
  plansUrl: 'https://sivapos.com/pricing',
};

/**
 * ลิงก์เปิดแชต LINE OA พร้อมพิมพ์ข้อความขอต่ออายุไว้ให้ — ร้านแค่กดส่ง
 * แนบชื่อร้าน + โดเมน (<slug>.sivapos.com) → ฝั่งเรารู้ทันทีว่าร้านไหน ไม่ต้องถามกลับ
 * ⚠️ ห้ามมีคำว่า "ทดลอง" — เป็นคีย์เวิร์ดตอบกลับอัตโนมัติของ OA (ปุ่มทดลองฟรีบนเว็บการตลาดใช้คำนี้)
 */
export function lineRenewUrl(shopName = '') {
  const text = ['ขอต่ออายุระบบ', shopName && `ร้าน ${shopName}`, window.location.host].filter(Boolean).join(' · ');
  return `https://line.me/R/oaMessage/${SUPPORT_CONTACT.line}/?${encodeURIComponent(text)}`;
}

const today = () => new Date().toISOString().slice(0, 10);

// กันยิง /v1/profile ซ้อนตอนหลาย response เจอแพ็กใหม่พร้อมกัน
// อยู่นอก state โดยตั้งใจ — store นี้ persist ถ้าปิดหน้ากลางคันค่าจะค้าง true แล้วไม่ refresh อีกเลย
let refreshingPlan = false;

/**
 * สถานะอายุการใช้งานระบบ + แพ็กของร้าน
 *
 * ค่าทั้งหมดมาจาก server เท่านั้น — FE ไม่คำนวณวันเอง เพราะนาฬิกาเครื่องลูกค้า
 * ตั้งย้อนหลังได้ (ดู §2 ของเอกสาร) เข้ามา 3 ทาง ไม่มีการยิง API เพิ่ม:
 *   1. header X-License-* ที่ติดมากับทุก response  → applyHeaders()
 *   2. ก้อน license ใน response ของ login          → applyPayload()
 *   3. 403 ERROR_LICENSE_EXPIRED                    → markLocked()
 *
 * แพ็ก (plan / features / limits) มากับก้อน license ตอน login (../silver-spoon-api/docs/plans-subscriptions.md §10)
 * header มีแค่ X-License-Plan — ถ้าไม่ตรงกับที่จำไว้ = แพ็กเปลี่ยนกลางคัน → checkPlan() ดึงก้อนใหม่จาก /v1/profile
 * (ยิงเฉพาะตอนนั้น ปกติ login เส้นเดียวจบ)
 */
export const useLicenseStore = defineStore('license', {
  state: () => ({
    // ชื่อ licenseState ไม่ใช่ state เฉย ๆ เพราะจะชนกับ $state ของ pinia จนอ่านโค้ดสับสน
    licenseState: 'active',   // active | expiring | expired | locked
    expiresOn: '',
    daysLeft: null,
    lockedOn: '',
    lastSyncedAt: null,
    dismissedOn: '',          // วันที่กดปิดแถบเตือนไปแล้ว (ปิดได้เฉพาะช่วงยังไม่ด่วน)

    // popup เตือนต่ออายุ (LicenseReminder) — ใกล้หมด: วันละครั้งต่อเครื่อง · ผ่อนผัน: ทุกครั้งที่เปิดแอป/ล็อกอิน
    remindedOn: '',           // วันที่กด "รับทราบ" ล่าสุด (persist — นับวันละครั้ง)
    remindedThisSession: false, // กดแล้วในรอบเปิดแอปนี้ (ไม่ persist — รีเฟรช/เปิดใหม่ = เด้งอีก)

    // แพ็กของร้าน · plan '' = ร้านไม่มีแพ็ก (API ส่ง state locked + features ทุกคีย์ false มาเอง)
    //                       หรือ API อ่าน license ไม่ได้ / API เก่า (features {} = ไม่กั้นอะไร)
    plan: '',
    planName: '',             // ชื่อแพ็กภาษาอังกฤษจาก API (Starter / Standard / Professional / Trial) — แปลด้วย planLabel()
    features: {},             // { feature_inventory: true, report_profit_report: false, ... }
    limits: {},               // { limit_branches: 3, limit_kds_stations: 0 } · null = ไม่จำกัด · KDS ไม่นับหน้ารวม

    // หน้า QR ลูกค้า: แพ็กไม่มี feature_qr_ordering (API ตอบ ERROR_FEATURE_NOT_AVAILABLE)
    // ลูกค้าไม่ได้ล็อกอิน จึงรู้แพ็กล่วงหน้าไม่ได้ ต้องรู้จาก error — ไม่ persist (ดู persist.omit)
    servicesUnavailable: false
  }),

  getters: {
    isLocked: (s) => s.licenseState === 'locked',

    isExpired: (s) => s.licenseState === 'expired',
    isExpiring: (s) => s.licenseState === 'expiring',

    /** ยังไม่ถึงขั้นล็อก แต่ต้องบอกให้รู้ */
    needsWarning: (s) => s.licenseState === 'expiring' || s.licenseState === 'expired',

    /**
     * แถบปิดได้ทุกช่วงที่เตือน (ใกล้หมด + ผ่อนผัน) — ปิดแล้วหายทั้งวัน พรุ่งนี้ขึ้นใหม่
     * เดิมเหลือ ≤ 3 วัน / ผ่อนผัน = ปิดไม่ได้ เพราะแถบเป็นตัวเตือนหลัก
     * ตอนนี้ popup (showReminder) รับหน้าที่เตือนหนักแทนแล้ว แถบที่ปิดไม่ได้เลยเป็นแค่สิ่งกวนตา (ตัดสิน 2026-09-29)
     */
    canDismiss() {
      return this.needsWarning;
    },

    showBanner() {
      if (this.isLocked) return false;               // ล็อกแล้วใช้หน้าล็อกแทน ไม่ต้องมีแถบซ้อน
      if (!this.needsWarning) return false;
      return !(this.canDismiss && this.dismissedOn === today());
    },

    /**
     * ล็อกแล้ว = คลุมทั้งจอ ไม่มีทางข้าม (แบบ Netflix — ../silver-spoon-api/docs/license-expiry.md §7.1)
     * API ก็ปิดทุกเส้นยกเว้น login / logout / profile อยู่แล้ว ต่อให้ลบ overlay ก็ไม่ได้ข้อมูล
     */
    showLockScreen() {
      return this.isLocked;
    },

    /**
     * popup เตือนต่ออายุ — ขึ้นตอนเปิดแอป ไม่ใช่ทุกครั้งที่เปลี่ยนหน้า
     * (modal ที่เด้งทุกหน้าจะถูกกดปิดแบบไม่อ่านภายในสองครั้ง แล้วกลายเป็นสิ่งที่คนมองไม่เห็น)
     *   ใกล้หมด (expiring) → วันละครั้งต่อเครื่อง
     *   ผ่อนผัน (expired)  → ทุกครั้งที่เปิดแอป / รีเฟรช / ล็อกอิน
     */
    showReminder() {
      if (this.isExpired) return !this.remindedThisSession;
      if (this.isExpiring) return this.remindedOn !== today() && !this.remindedThisSession;
      return false;
    },

    /**
     * แพ็กมีฟีเจอร์/รายงานนี้ไหม (feature_* / report_*)
     * คีย์ที่ไม่มี (ไม่รู้จัก / API อ่าน license ไม่ได้) = ใช้ได้ — ตรงกับ FeatureMiddleware ฝั่ง API
     * ตัวบังคับจริงอยู่ที่ API ฝั่งนี้แค่ซ่อนเมนู/ปุ่ม
     */
    hasFeature: (s) => (slug) => !slug || !(slug in s.features) || s.features[slug] === true,

    /** เพดานของ limit_* · null = ไม่จำกัด (limit_branches รวมสาขาเพิ่มแล้ว) */
    limitOf: (s) => (slug) => (slug in s.limits ? s.limits[slug] : null)
  },

  actions: {
    /**
     * อ่านจาก header ของทุก response (axios แปลงชื่อ header เป็นตัวพิมพ์เล็กให้แล้ว)
     * response ที่ไม่ได้ผ่าน middleware ของเรา (เช่น error ที่โยนก่อนถึง) จะไม่มี header
     * → เงียบไว้ ไม่แตะค่าเดิม ดีกว่าเดาแล้วรีเซ็ตสถานะทิ้ง
     */
    applyHeaders(headers) {
      if (!headers) return;
      const next = headers['x-license-state'];
      if (!next) return;

      this.licenseState = next;
      this.expiresOn = headers['x-license-expires-on'] || '';
      this.lockedOn = headers['x-license-locked-on'] || '';
      const days = headers['x-license-days-left'];
      this.daysLeft = days === '' || days === undefined || days === null ? null : Number(days);
      this.lastSyncedAt = new Date().toISOString();
    },

    /** ก้อน license ที่แนบมากับ response ของ login */
    applyPayload(license) {
      if (!license) return;

      this.applyPlan(license);

      this.licenseState = license.state || 'active';
      this.expiresOn = license.expires_on || '';
      this.lockedOn = license.locked_on || '';
      this.daysLeft = license.days_left === null || license.days_left === undefined
        ? null
        : Number(license.days_left);
      this.lastSyncedAt = new Date().toISOString();
      // ล็อกอินใหม่ = รอบเปิดแอปใหม่ → ช่วงผ่อนผันต้องเห็น popup อีกครั้ง
      this.remindedThisSession = false;
    },

    /** เจอ 403 ERROR_LICENSE_EXPIRED — เชื่อทันที ไม่ต้องรอ header รอบถัดไป */
    markLocked(expiresOn) {
      this.licenseState = 'locked';
      if (expiresOn) this.expiresOn = expiresOn;
      this.lastSyncedAt = new Date().toISOString();
    },

    /** แพ็กจากก้อน license (login / profile) · API เวอร์ชันก่อนระบบแพ็กไม่มีคีย์เหล่านี้ = ไม่กั้นอะไร */
    applyPlan(license) {
      if (!license) return;

      this.plan = license.plan || '';
      this.planName = license.plan_name || '';
      this.features = license.features || {};
      this.limits = license.limits || {};
    },

    /**
     * เทียบ X-License-Plan กับที่จำไว้ — ไม่ตรง = แพ็กเปลี่ยนกลางคัน → ดึงก้อนใหม่
     * interceptor เป็นคนเรียก และเรียกเฉพาะตอนล็อกอินอยู่ (ไม่งั้น /v1/profile ได้ 401 แล้วโดนเด้งออก)
     * header ไม่มี (API เก่า / error ก่อนถึง middleware) = เงียบไว้
     */
    checkPlan(headerPlan) {
      if (headerPlan === undefined || headerPlan === null) return;
      // แพ็กตรงแต่ยังไม่มีชื่อ = store จากเวอร์ชันก่อนมี plan_name (ล็อกอินค้างข้าม deploy) → ดึงครั้งเดียวเหมือนแพ็กเปลี่ยน
      const upToDate = headerPlan === this.plan && (this.planName !== '' || headerPlan === '');
      if (upToDate || refreshingPlan) return;

      this.refreshPlan();
    },

    async refreshPlan() {
      refreshingPlan = true;
      try {
        // import ตอนใช้ — กัน import วน (router ↔ store ↔ service) และให้เทสต์ของ store ไม่ต้องลาก axios มา
        const { Auth } = await import('@/services/endpoint/auth-api.service');
        const res = await Auth.getProfile();
        this.applyPlan(res?.data?.license);
      } catch (e) {
        // เงียบไว้ — response ถัดไปที่ header ยังไม่ตรงจะลองใหม่เอง
      } finally {
        refreshingPlan = false;
      }
    },

    /** interceptor เรียก — เส้น /v1/services/* ตอบ ERROR_FEATURE_NOT_AVAILABLE / ตอบสำเร็จ */
    setServicesUnavailable(value) {
      this.servicesUnavailable = value;
    },

    dismissBanner() {
      if (this.canDismiss) this.dismissedOn = today();
    },

    /** กด "รับทราบ" บน popup — ใช้งานต่อได้ (ช่วงผ่อนผันจะเด้งอีกตอนเปิดแอปครั้งถัดไป) */
    dismissReminder() {
      this.remindedOn = today();
      this.remindedThisSession = true;
    }
  },

  // servicesUnavailable ไม่เก็บ — ค้างในเครื่องลูกค้าแล้วร้านอัปเกรดทีหลัง = ลูกค้าเห็นจอปิดทั้งที่สั่งได้แล้ว
  // remindedThisSession ไม่เก็บ — ความหมายคือ "รอบเปิดแอปนี้" รีเฟรชแล้วต้องเริ่มใหม่
  persist: {
    omit: ['servicesUnavailable', 'remindedThisSession']
  }
});
